import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Globe, Check, ChevronDown } from "lucide-react";
import { setLocale, SUPPORTED_LOCALES } from "./index";
import { api } from "../lib/api";

const LABELS = { en: "English", fr: "Français" };
const SHORT = { en: "EN", fr: "FR" };

/**
 * Global language toggle.
 *
 * Variants:
 *   - "header" (default): pill with short locale label, used in AppLayout
 *   - "compact": text-only "EN" / "FR" toggle, used on public auth pages
 *
 * If a user is signed in, the new locale is persisted server-side via
 * PATCH /api/auth/me/locale (silent — no toast). Otherwise the choice is
 * kept in localStorage only.
 */
export default function LanguageSwitcher({ variant = "header", testIdPrefix, className }) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = SUPPORTED_LOCALES.includes(i18n.language?.split("-")[0])
    ? i18n.language.split("-")[0]
    : "en";

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const pick = async (loc) => {
    if (loc === current) {
      setOpen(false);
      return;
    }
    setLocale(loc);
    setOpen(false);
    // Best-effort server sync — silent failure is fine; localStorage holds the truth.
    try {
      await api.patch("/auth/me/locale", { locale: loc });
    } catch (_) { /* unauth or offline — ignore */ }
  };

  if (variant === "compact") {
    return (
      <div className="inline-flex gap-1 text-[11px] font-medium" data-testid="language-switcher-compact">
        {SUPPORTED_LOCALES.map((loc) => (
          <button
            key={loc}
            onClick={() => pick(loc)}
            data-testid={`language-switcher-option-${loc}`}
            className={`px-2 py-1 rounded-md tracking-wide transition-colors ${
              current === loc
                ? "bg-zinc-950 text-white"
                : "text-zinc-500 hover:text-zinc-900"
            }`}
          >
            {SHORT[loc]}
          </button>
        ))}
      </div>
    );
  }

  if (variant === "sidebar-toggle") {
    // Iter69 — dedicated sidebar block with two clear FR/EN pills. Sits on
    // top of the QENVA navy surface (bg-qenva-navy) so we invert the colour
    // scheme: subtle white-alpha inactive, brand blue for the active locale.
    const prefix = testIdPrefix || "language-switcher";
    return (
      <div className={className || ""} data-testid={prefix}>
        <div
          className="grid grid-cols-2 gap-1 p-1 rounded-lg bg-white/5 border border-white/10"
          data-testid={`${prefix}-toggle-group`}
        >
          {SUPPORTED_LOCALES.map((loc) => (
            <button
              key={loc}
              onClick={() => pick(loc)}
              type="button"
              data-testid={`${prefix}-option-${loc}`}
              aria-pressed={current === loc}
              className={`h-8 rounded-md text-[12px] font-semibold tracking-wide transition-colors ${
                current === loc
                  ? "bg-qenva-blue text-white shadow-sm"
                  : "text-white/70 hover:text-white hover:bg-white/5"
              }`}
            >
              {SHORT[loc]}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div ref={ref} className={`relative ${className || ""}`} data-testid={testIdPrefix || "language-switcher"}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`${testIdPrefix || "language-switcher"}-trigger`}
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-zinc-200 bg-white hover:border-zinc-300 transition-colors text-[12px] font-medium text-zinc-700"
      >
        <Globe size={13} strokeWidth={1.75} />
        <span data-testid={`${testIdPrefix || "language-switcher"}-current`}>{SHORT[current]}</span>
        <ChevronDown size={12} className="text-zinc-400" />
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-1 z-50 w-36 rounded-lg border border-zinc-200 bg-white shadow-lg overflow-hidden"
          data-testid={`${testIdPrefix || "language-switcher"}-menu`}
        >
          {SUPPORTED_LOCALES.map((loc) => (
            <button
              key={loc}
              onClick={() => pick(loc)}
              data-testid={`${testIdPrefix || "language-switcher"}-option-${loc}`}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-zinc-50 flex items-center justify-between gap-2 ${
                current === loc ? "bg-zinc-50 font-medium" : ""
              }`}
            >
              <span>{LABELS[loc]}</span>
              {current === loc && <Check size={13} className="text-emerald-600" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
